import { useEffect, useMemo, useRef, useState } from "react";
import { useStore, useWorkshop } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { IdentityForm } from "@/workshop/IdentityForm";
import { api } from "@/api/client";
import type { Team } from "@offsite/shared";
import { WaitingScreen } from "@/workshop/WaitingScreen";
import { TeamWorkspace } from "@/workshop/TeamWorkspace";
import { EvaluationView } from "@/workshop/EvaluationView";
import { EvaluationProgress } from "@/workshop/EvaluationProgress";
import { OutputView } from "@/workshop/OutputView";
import { VoteScreen } from "@/workshop/VoteScreen";
import { ParticipantTeamView } from "@/workshop/ParticipantTeamView";
import { MobileSubmittedHero } from "@/workshop/MobileSubmittedHero";
import { MobileFrame } from "@/components/MobileFrame";
import { LogoutButton } from "@/components/LogoutButton";
import { useOvernightStatus } from "@/hooks/useOvernightStatus";

/**
 * Participant-Surface (Mobile / `/m/*`).
 *
 * Wrapped in <MobileFrame> (Beacon-Light-Skin). Inhalt phase-gebunden.
 * Phase=submitted → MobileSubmittedHero (das eine erlaubte --signal-ok Grün).
 *
 * Bestehende Inner-Views (WaitingScreen, VoteScreen, TeamWorkspace, …)
 * werden bis zum vollständigen Beacon-Refactor weitergenutzt; sie sind
 * via Token-Migration (PR1) bereits auf Beacon-Farben umgestellt.
 */
export function ParticipantPage() {
  const { phase } = useWorkshop();
  const { state } = useStore();
  const token = state.auth.token;
  const [myTeamNumber, setMyTeamNumber] = useState<number | null>(null);
  const [resultId, setResultId] = useState<string | null>(null);
  /**
   * Team des Code-Logins — für die Identitäts-Abfrage direkt nach dem
   * Login. Vorher hing das "Wer bist du?"-Formular im TeamWorkspace und
   * erschien erst mit dem Prompten: die drei Minuten Anmeldezeit während
   * Warten/Einführung verpufften, und die Arbeitsphase begann für alle mit
   * Name-Tippen (QA-Fund 14.09.).
   */
  const [meinTeam, setMeinTeam] = useState<Team | null>(null);
  const [submittedAt, setSubmittedAt] = useState<number | undefined>(undefined);
  const config = useOffsiteConfig();
  const { soloTeams, overnightWaitLabel, einzelmodus } = config;
  const soloJoined = useRef(false);

  // Fortschritt der Generierung. Ein Wartebildschirm ohne Zahl lässt offen, ob
  // überhaupt etwas passiert — und in einer Sitzung, in der das Ergebnis zwei
  // Minuten später auf der Leinwand steht, sind zwei Minuten Stille lang.
  const bau = useOvernightStatus(phase.phase === "overnight");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        // Solo-Offsites kennen kein Voting — die Teamnummer kommt aus dem
        // (idempotenten) Beitritt. Fehlschläge zeigt TeamWorkspace an, das
        // dieselbe Zuordnung für den Editor braucht.
        if (soloTeams) {
          if (soloJoined.current) return;
          const r = await api.teams.join(token);
          soloJoined.current = true;
          if (!cancelled) {
            setMyTeamNumber(r.team.number);
            // Code-Login-Teams (Bühne/Hintergrund) bekommen beim Join eine
            // resultId — der dauerhafte, persönliche Ergebnis-Link.
            setResultId(r.team.resultId ?? null);
            setMeinTeam(r.team);
          }
          return;
        }
        const v = await api.votes.me(token);
        if (cancelled) return;
        setMyTeamNumber(v.vote?.teamNumber ?? null);
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token, soloTeams]);

  // Wenn die Phase auf "submitted" wechselt, einen lokalen Zeitstempel setzen.
  useEffect(() => {
    if (phase.phase === "submitted" && submittedAt === undefined) {
      setSubmittedAt(Date.now());
    }
    if (phase.phase === "working") {
      setSubmittedAt(undefined);
    }
  }, [phase.phase, submittedAt]);

  /**
   * Eingereicht-Screen mit dem persönlichen QR-Link — steht im Einzelmodus ab
   * der Generierung bis zum Gruppenwechsel auf dem Handy (Briefing Prompt-Teil
   * 3: Generierung, Ergebnisse und Abschluss zeigen alle denselben Screen).
   */
  const eingereichtScreen = (recap: string) =>
    einzelmodus && resultId ? (
      <MobileSubmittedHero
        submittedAt={submittedAt}
        recap={recap}
        kicker="DIE KI BAUT"
        resultUrl={`${window.location.origin}/ergebnis/${resultId}`}
      />
    ) : null;

  // Pflicht-Identität vor allem anderen: Name + Abteilung direkt nach dem
  // Login abfragen, in jeder Phase vor der Generierung — nicht erst, wenn
  // das Prompten öffnet.
  const identityFehlt =
    einzelmodus &&
    config.identity !== undefined &&
    meinTeam !== null &&
    (!meinTeam.name || !meinTeam.abteilung) &&
    ["waiting", "einfuehrung", "working"].includes(phase.phase);

  const content = useMemo(() => {
    if (identityFehlt && meinTeam) {
      return <IdentityForm n={meinTeam.number} onDone={(t) => setMeinTeam(t)} />;
    }
    switch (phase.phase) {
      case "waiting":
        return <WaitingScreen />;
      case "einfuehrung":
        // Rahmenfolien laufen vorn — das Handy zeigt Login bzw. Wartetext.
        return <WaitingScreen />;
      case "abschluss":
        return (
          eingereichtScreen(
            "Dein Ergebnis bleibt unter deinem Link erreichbar — auch in vier Wochen noch.",
          ) ?? <WaitingScreen />
        );
      case "topics":
        return <WaitingScreen label="Themen werden gleich zur Abstimmung freigeschaltet." />;
      case "voting":
        return <VoteScreen />;
      case "winner-reveal":
        return <WaitingScreen label="Gewinner steht fest! Gleich werden die Teams verteilt." />;
      case "grouping":
        return <ParticipantTeamView />;
      case "working":
        return <TeamWorkspace />;
      case "submitted":
        return (
          <MobileSubmittedHero
            submittedAt={submittedAt}
            recap="Die KI bewertet eure Anforderung in den nächsten Minuten."
            resultUrl={
              resultId
                ? `${window.location.origin}/ergebnis/${resultId}`
                : undefined
            }
          />
        );
      case "evaluating":
        return <EvaluationProgress />;
      case "evaluated":
        return myTeamNumber ? (
          <EvaluationView teamNumber={myTeamNumber} />
        ) : (
          <EvaluationProgress />
        );
      case "overnight":
        return (
          eingereichtScreen(
            overnightWaitLabel ?? "Die KI baut Ihren Prototyp.",
          ) ?? (
          <WaitingScreen
            label={overnightWaitLabel ?? "Die KI baut über Nacht – bis morgen früh."}
            zusatz={
              bau
                ? bau.status === "complete" ||
                  (bau.total > 0 && bau.done >= bau.total)
                  ? "Fertig — gleich auf der Leinwand."
                  : `${bau.done} von ${bau.total || "…"} fertig`
                : undefined
            }
          />
          )
        );
      case "reveal":
        // Einzelmodus: die Bühne läuft auf der Leinwand, der Hintergrund
        // findet sein Ergebnis unter dem eigenen Link — das Handy behält den
        // Eingereicht-Screen mit QR.
        return (
          eingereichtScreen(
            "Dein Ergebnis findest du unter deinem Link — auch später noch.",
          ) ??
          (myTeamNumber ? (
            <OutputView teamNumber={myTeamNumber} />
          ) : (
            <WaitingScreen />
          ))
        );
      default:
        return <WaitingScreen />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.phase, myTeamNumber, resultId, submittedAt, overnightWaitLabel, bau, einzelmodus, identityFehlt, meinTeam]);

  return (
    <MobileFrame timerDeadline={phase.timerEndsAt}>
      {/* Logout auch im Einzelmodus (Wunsch 14.09.) — dezent oben rechts,
          z. B. wenn jemand das falsche Gerät oder den falschen Code erwischt. */}
      <div
        className="participant-toolbar"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "var(--s-3)",
        }}
      >
        <LogoutButton />
      </div>
      {content}
    </MobileFrame>
  );
}
